import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { subscribeHabitChanges } from "@/lib/habitChanges";

const XP_PER_LEVEL = 500;

export interface XPState {
  totalXP: number;
  level: number;
  loading: boolean;
}

export interface XPEvent {
  id: string;
  source: string;
  action: string;
  xp_amount: number;
  created_at: string;
}

export interface XPOwnerGuard { ownerId: string; isCurrent: () => boolean }

export const useXP = (options?: { guarded?: boolean }) => {
  const guarded = options?.guarded === true;
  const mounted = useRef(false), owner = useRef<string | null | undefined>(undefined), generation = useRef(0), reads = useRef(0);
  const [state, setState] = useState<XPState>({ totalXP: 0, level: 1, loading: true });
  const stateRef = useRef(state);
  stateRef.current = state;

  const fetchXP = useCallback(async (ownerGuard?: XPOwnerGuard) => {
    const safeRead = guarded || !!ownerGuard, ticket = ++reads.current;
    const epoch = generation.current; let uid = owner.current;
    const current = () => !safeRead || (mounted.current && ticket === reads.current && epoch === generation.current &&
      (!guarded || owner.current === uid) && (!ownerGuard || ownerGuard.isCurrent()));
    if (!current()) return false;
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (!current()) return false;
      if (safeRead && (userError || (ownerGuard && user?.id !== ownerGuard.ownerId))) { setState(s => ({ ...s, loading: false })); return false; }
      if (guarded && owner.current === undefined) { uid = user?.id ?? null; owner.current = uid; }
      if (guarded && owner.current !== (user?.id ?? null)) return false;
      if (!user) { setState(s => ({ ...s, loading: false })); return true; }
      let token: string | null = null;
      if (safeRead) {
        const session = await supabase.auth.getSession();
        if (!current()) return false;
        if (session.error || session.data.session?.user.id !== user.id || !session.data.session.access_token) { setState(s => ({ ...s, loading: false })); return false; }
        token = session.data.session.access_token;
      }
      const query = supabase.from("user_xp").select("total_xp, level").eq("user_id", user.id).maybeSingle();
      if (token) query.setHeader("Authorization", "Bearer " + token);
      if (!current()) return false;
      const { data, error } = await query;
      if (!current()) return false;
      if (error) { console.error("fetchXP read unavailable"); setState(s => ({ ...s, loading: false })); return false; }
      if (data) {
        if (safeRead && (!Number.isSafeInteger(data.total_xp) || !Number.isSafeInteger(data.level))) { setState(s => ({ ...s, loading: false })); return false; }
        setState({ totalXP: data.total_xp, level: data.level, loading: false });
      } else {
        // The new guarded read never initializes or awards XP. Legacy callers
        // retain their existing initialization behavior.
        if (!safeRead) await supabase.from("user_xp").insert({ user_id: user.id, total_xp: 0, level: 1 });
        if (!current()) return false;
        setState({ totalXP: 0, level: 1, loading: false });
      }
      return true;
    } catch { if (current()) setState(s => ({ ...s, loading: false })); return false; }
  }, [guarded]);

  useEffect(() => {
    mounted.current = true;
    if (!guarded) { void fetchXP(); return () => { mounted.current = false; }; }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (!mounted.current || owner.current === next) return;
      owner.current = next; generation.current++; reads.current++;
      const epoch = generation.current;
      setState({ totalXP: 0, level: 1, loading: !!next });
      if (next) queueMicrotask(() => { if (mounted.current && owner.current === next && generation.current === epoch) void fetchXP(); });
    });
    const unsubscribe = subscribeHabitChanges(uid => { if (mounted.current && owner.current === uid) void fetchXP(); });
    void fetchXP();
    const lifetime = generation, pendingReads = reads;
    return () => { mounted.current = false; lifetime.current++; pendingReads.current++; subscription.unsubscribe(); unsubscribe(); };
  }, [fetchXP, guarded]);

  const addXP = useCallback(async (
    source: string,
    action: string,
    amount: number,
    referenceId?: string,
    ownerGuard?: XPOwnerGuard
  ): Promise<{ inserted: boolean; newTotal: number; newLevel: number; leveledUp: boolean }> => {
    const current = stateRef.current;
    const declined = () => ({ inserted: false, newTotal: current.totalXP, newLevel: current.level, leveledUp: false });
    if (ownerGuard && !ownerGuard.isCurrent()) return declined();
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user || (ownerGuard && (!ownerGuard.isCurrent() || user.id !== ownerGuard.ownerId))) return declined();
    let pinnedToken: string | null = null;
    if (ownerGuard) {
      const session = await supabase.auth.getSession();
      if (!ownerGuard.isCurrent() || session.error || session.data.session?.user.id !== ownerGuard.ownerId || !session.data.session.access_token) return declined();
      pinnedToken = session.data.session.access_token;
    }
    const request = supabase.rpc("add_xp_atomic", {
      p_source: source,
      p_action: action,
      p_amount: amount,
      p_reference_id: referenceId ?? null,
    });
    // PostgREST preserves an explicit Authorization header even if its SDK auth
    // transport awaits a newer session. The award stays tied to this owner.
    if (pinnedToken) request.setHeader("Authorization", "Bearer " + pinnedToken);
    if (ownerGuard && !ownerGuard.isCurrent()) return declined();
    const { data, error } = await request;
    if (ownerGuard && !ownerGuard.isCurrent()) return declined();

    if (error) {
      console.error("addXP RPC error:", error);
      return { inserted: false, newTotal: current.totalXP, newLevel: current.level, leveledUp: false };
    }

    const result = Array.isArray(data) ? data[0] : data;
    if (!result) {
      return { inserted: false, newTotal: current.totalXP, newLevel: current.level, leveledUp: false };
    }

    setState({
      totalXP: result.total_xp,
      level: result.level,
      loading: false,
    });

    return {
      inserted: result.inserted,
      newTotal: result.total_xp,
      newLevel: result.level,
      leveledUp: result.leveled_up,
    };
  }, []);

  const getLevelProgress = useCallback((): number => {
    const xpInCurrentLevel = state.totalXP % XP_PER_LEVEL;
    return (xpInCurrentLevel / XP_PER_LEVEL) * 100;
  }, [state.totalXP]);

  const getXPToNextLevel = useCallback((): number => {
    return XP_PER_LEVEL - (state.totalXP % XP_PER_LEVEL);
  }, [state.totalXP]);

  return {
    ...state,
    addXP,
    getLevelProgress,
    getXPToNextLevel,
    refresh: fetchXP,
  };
};
